import React, { useMemo, useState } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import camera from 'static/camera.svg';
import { Container } from '~/components/layout';
import { toBase64 } from '~/utils/files';
import { LmiDocumentTypeSelect } from '../dropdowns/lmi-document-type/lmi-document-type-select.component';
import { ImagePreview } from './image-preview';
import { ImageReviewDialog } from './image-review-dialog';
import { Dropzone } from './image-uploader.style';

export interface ImageUploaderProps {
  acceptFileTypes: { [key: string]: string[] };
  copy: { documentType: string };
  uploadedImages: any[];
  maxImages: number;
  pageType: 'numbered' | 'front-back' | 'multiple';
  pending: boolean;
  onUploadSuccess: (type: string, file: File, base64Image: string) => void;
  onUploadFailure: (type: string, file: FileRejection) => void;
  onDelete: (id: string) => void;
}

type ImageFile = {
  file: File;
  base64Image: string;
};

export const ImageTypeUploader = ({
  acceptFileTypes,
  copy,
  uploadedImages,
  maxImages,
  pageType,
  onDelete,
  onUploadFailure,
  onUploadSuccess,
  pending,
}: ImageUploaderProps) => {
  const [reviewImageFile, setReviewImageFile] = useState<ImageFile | null>(null);
  const [selectedType, setSelectedType] = useState<string>('');

  const totalSavedFiles = uploadedImages.filter(f => f.uploadId).length;

  const onDrop = async (accepted: File[], rejected: FileRejection[]) => {
    if (accepted.length) {
      const file = accepted[0];
      const base64Image = await toBase64(file);
      setReviewImageFile({ file, base64Image });
    } else if (rejected.length) {
      const rejectedFile = rejected[0];
      onUploadFailure(selectedType, rejectedFile);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: acceptFileTypes,
    maxSize: 5 * 1000 * 1000, // 5 MB
    maxFiles: maxImages,
    multiple: false,
    onDrop,
    validator: (file: File) => {
      const alreadyUploaded = uploadedImages.map(f => f.file.docName).includes(file.name);
      return alreadyUploaded ? { code: 'already-uploaded', message: 'Already uploaded' } : null;
    },
  });

  const uploadNextPageMessage = () => {
    let nextPageDescription: string;
    if (pageType === 'front-back') {
      nextPageDescription = totalSavedFiles ? 'Back' : 'Front';
    } else if (pageType === 'numbered') {
      nextPageDescription = `Page ${totalSavedFiles + 1}`;
    } else {
      nextPageDescription = '';
    }
    return `Upload ${copy.documentType} ${nextPageDescription && `(${nextPageDescription})`}`;
  };

  const lmiDocumentTypeOptions = useMemo(() => {
    const docTypes = {
      card: 'Card',
      letter: 'Letter',
      utilityBill: 'Utility bill',
      accountStatement: 'Portal screenshot',
    };
    return Object.keys(docTypes).map(typeKey => {
      return {
        disabled: false,
        selected: false,
        text: docTypes[typeKey],
        value: typeKey,
      };
    });
  }, []);

  return (
    <>
      <Container display="flex" flexDirection="column">
        {uploadedImages.map(({ proofType, file }) => {
          return file ? (
            <React.Fragment key={file.id}>
              <LmiDocumentTypeSelect
                options={lmiDocumentTypeOptions}
                value={proofType}
                disabled={file.error !== undefined || !!proofType}
                onChange={value => setSelectedType(value)}
              />
              <ImagePreview
                errorCode={file.error}
                key={file.id}
                onDelete={() => onDelete(file.id)}
                base64Image={file.base64Image}
                uploaded={file.error === undefined && !!file.id}
              />
            </React.Fragment>
          ) : null;
        })}
      </Container>
      {!pending && (
        <>
          <LmiDocumentTypeSelect
            options={lmiDocumentTypeOptions}
            value={selectedType}
            disabled={false}
            onChange={value => setSelectedType(value)}
          />
          {selectedType && (
            <Dropzone {...getRootProps()}>
              <input data-testid="dropzone" {...getInputProps()} />
              <Container pt={1}>
                <img src={camera} />
              </Container>
              <b>{isDragActive ? 'Drop to upload' : uploadNextPageMessage()}</b>
            </Dropzone>
          )}
        </>
      )}
      {reviewImageFile && (
        <ImageReviewDialog
          base64Image={reviewImageFile.base64Image}
          fileType={reviewImageFile.file.type}
          onApprove={base64Image => {
            onUploadSuccess(selectedType, reviewImageFile.file, base64Image);
            setReviewImageFile(null);
            setSelectedType('');
          }}
          onCancel={() => setReviewImageFile(null)}
        />
      )}
    </>
  );
};
